import { SSF } from "xlsx";
import {
  JsonSchemaItems,
  PrimitiveValueType,
  RowDataValueType,
} from "../../../../types";
import { getInputType } from "../schemaHelpers";

export const formatExcelValue = (
  value: RowDataValueType,
  propertyType: string,
  format?: string,
  items?: JsonSchemaItems,
): RowDataValueType => {
  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  if (propertyType === "string" && format === "date") {
    return formatDateValue(value);
  }

  if (propertyType === "boolean") {
    return formatBooleanValue(value);
  }

  if (propertyType === "number" || propertyType === "integer") {
    return formatNumericValue(value, propertyType);
  }

  if (propertyType === "string") {
    return formatStringValue(value);
  }

  if (propertyType === "array") {
    return formatArrayValue(value, items);
  }

  return value;
};

const formatArrayValue = (
  value: RowDataValueType,
  items?: JsonSchemaItems,
): PrimitiveValueType[] | RowDataValueType => {
  if (typeof value !== "string" || !items?.type) {
    return value;
  }
  const itemType = getInputType(items.type);
  return value
    .split(",")
    .map(
      (item) => formatExcelValue(item.trim(), itemType) as PrimitiveValueType,
    );
};

const formatDateValue = (value: RowDataValueType): RowDataValueType => {
  if (typeof value !== "number") {
    return value == null ? null : String(value);
  }
  const date = SSF.parse_date_code(value);
  return date
    ? `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(
        2,
        "0",
      )}`
    : value;
};

const formatBooleanValue = (value: RowDataValueType): RowDataValueType => {
  const BOOLEAN_TRUE_VALUES = new Set(["true", "yes", "y", "1"]);
  const BOOLEAN_FALSE_VALUES = new Set(["false", "no", "n", "0"]);
  if (typeof value !== "string") {
    return value;
  }
  const normalizedValue = value.trim().toLowerCase();
  if (BOOLEAN_TRUE_VALUES.has(normalizedValue)) {
    return true;
  }
  if (BOOLEAN_FALSE_VALUES.has(normalizedValue)) {
    return false;
  }
  return value;
};

const formatNumericValue = (
  value: RowDataValueType,
  propertyType: string,
): RowDataValueType => {
  if (typeof value !== "string") {
    return value;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }
  if (propertyType === "integer") {
    return Number.isInteger(num) ? num : value;
  }
  return num;
};

const formatStringValue = (value: RowDataValueType): RowDataValueType => {
  return value == null ? null : String(value);
};
